<template>
    <div class="co-page">
        <!-- ────────── HEADER ────────── -->
        <header class="co-header">
            <div class="co-header__inner">
                <div class="co-header__title-wrap">
                    <h1 class="co-title">קהילות</h1>
                    <span class="co-total-pill" :title="`סה״כ ${communities.length} קהילות`">
                        <v-icon size="14">mdi-account-group-outline</v-icon>
                        <span>{{ communities.length.toLocaleString('he-IL') }}</span>
                    </span>
                </div>
                <button type="button" class="co-cta" @click="openCreateDIalog()">
                    <v-icon size="17">mdi-plus</v-icon>
                    <span>הוסף קהילה</span>
                </button>
            </div>
        </header>

        <!-- ────────── MAIN ────────── -->
        <main class="co-main">

            <!-- STATS (also quick filters) -->
            <section class="co-stats" aria-label="סיכום קהילות">
                <button type="button" class="co-stat co-stat--all"
                    :class="{ 'co-stat--active': quickFilter === 'all' }" @click="setQuickFilter('all')">
                    <div class="co-stat__icon"><v-icon size="19">mdi-account-group-outline</v-icon></div>
                    <div class="co-stat__body">
                        <span class="co-stat__label">כל הקהילות</span>
                        <strong class="co-stat__value">{{ communities.length.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>

                <div class="co-stat co-stat--levels co-stat--static">
                    <div class="co-stat__icon"><v-icon size="19">mdi-filter-variant</v-icon></div>
                    <div class="co-stat__body">
                        <span class="co-stat__label">רמות סינון</span>
                        <strong class="co-stat__value">{{ totalLevels.toLocaleString('he-IL') }}</strong>
                    </div>
                </div>

                <button type="button" class="co-stat co-stat--warn"
                    :class="{ 'co-stat--active': quickFilter === 'noLevels' }" @click="setQuickFilter('noLevels')">
                    <div class="co-stat__icon"><v-icon size="19">mdi-filter-variant-remove</v-icon></div>
                    <div class="co-stat__body">
                        <span class="co-stat__label">ללא רמות סינון</span>
                        <strong class="co-stat__value">{{ noLevelsCount.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>

                <button type="button" class="co-stat co-stat--sync"
                    :class="{ 'co-stat--active': quickFilter === 'notSynced' }" @click="setQuickFilter('notSynced')">
                    <div class="co-stat__icon"><v-icon size="19">mdi-sync-alert</v-icon></div>
                    <div class="co-stat__body">
                        <span class="co-stat__label">לא מסונכרנות עם לחומרא</span>
                        <strong class="co-stat__value">{{ notSyncedCount.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>
            </section>

            <!-- TOOLBAR -->
            <div class="co-toolbar">
                <div class="co-search">
                    <v-icon class="co-search__icon" size="19">mdi-magnify</v-icon>
                    <input v-model="search" type="text" class="co-search__input"
                        placeholder="חיפוש לפי שם, שם במערכת לחומרא, מזהה או טוקן…" />
                    <button v-if="search" type="button" class="co-search__clear" @click="search = ''"
                        aria-label="נקה חיפוש">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </div>
                <div class="co-toolbar__end">
                    <span v-if="quickFilter !== 'all'" class="co-filter-chip">
                        <v-icon size="14">mdi-filter-outline</v-icon>
                        {{ quickFilterLabel }}
                        <button type="button" class="co-filter-chip__clear" @click="setQuickFilter('all')"
                            aria-label="הסר סינון">
                            <v-icon size="13">mdi-close</v-icon>
                        </button>
                    </span>
                    <div class="co-pagesize">
                        <label for="co-pagesize-select">שורות</label>
                        <select id="co-pagesize-select" class="co-pagesize__select" v-model.number="itemsPerPage">
                            <option v-for="n in [20, 50, 100]" :key="n" :value="n">{{ n }}</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- TABLE -->
            <div class="co-table-card">
                <v-data-table id="table1" :headers="headers" :items="filteredCommunities" hide-default-footer
                    :search="search" :page.sync="currentPage" :items-per-page="itemsPerPage"
                    @page-count="pageCount = $event" class="co-table">

                    <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                        <span class="co-th">{{ header.text }}</span>
                    </template>

                    <template v-slot:item.index="{ index }">
                        <span class="co-index">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
                    </template>

                    <template v-slot:item.name="{ item }">
                        <div class="co-name-cell">
                            <span class="co-avatar">{{ initial(item.name) }}</span>
                            <span class="co-name">{{ item.name }}</span>
                        </div>
                    </template>

                    <template v-slot:item.external_name="{ item }">
                        <span v-if="item.external_name" class="co-ext-name">{{ item.external_name }}</span>
                        <span v-else class="co-muted">—</span>
                    </template>

                    <template v-slot:item.external_id="{ item }">
                        <span v-if="item.external_id" class="co-num-cell">{{ item.external_id }}</span>
                        <span v-else class="co-muted">—</span>
                    </template>

                    <template v-slot:item.token="{ item }">
                        <div v-if="item.token" class="co-token">
                            <code class="co-token__value">{{ revealedTokens[item._id] ? item.token : maskToken(item.token) }}</code>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="co-token__btn" @click="toggleToken(item)"
                                        :aria-label="revealedTokens[item._id] ? 'הסתר טוקן' : 'הצג טוקן'">
                                        <v-icon size="15">{{ revealedTokens[item._id] ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                                    </button>
                                </template>
                                <span>{{ revealedTokens[item._id] ? 'הסתר' : 'הצג' }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="co-token__btn" @click="copyToken(item)"
                                        aria-label="העתק טוקן">
                                        <v-icon size="15">mdi-content-copy</v-icon>
                                    </button>
                                </template>
                                <span>העתק</span>
                            </v-tooltip>
                        </div>
                        <span v-else class="co-muted">—</span>
                    </template>

                    <template v-slot:item.levels="{ item }">
                        <v-tooltip bottom max-width="320" :disabled="!levelsCount(item)">
                            <template v-slot:activator="{ on }">
                                <button v-on="on" type="button" class="co-levels-chip"
                                    :class="{ 'co-levels-chip--empty': !levelsCount(item) }"
                                    @click="openEditDialog(item)">
                                    <v-icon size="13">mdi-filter-variant</v-icon>
                                    {{ levelsLabel(item) }}
                                </button>
                            </template>
                            <div class="co-levels-tip">
                                <div v-for="level in sortedLevels(item)" :key="level._id" class="co-levels-tip__row">
                                    <span class="co-levels-tip__code">{{ level.level_code }}</span>
                                    <span>{{ level.level_name }}</span>
                                </div>
                            </div>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.actions="{ item, index }">
                        <div class="co-actions">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="co-action-btn co-action-btn--edit"
                                        @click="openEditDialog(item, index)" aria-label="עריכה">
                                        <v-icon size="17">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עריכה ורמות סינון</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="co-action-btn co-action-btn--delete"
                                        @click="openDeleteDialog(item, index)" aria-label="מחיקה">
                                        <v-icon size="17">mdi-delete-outline</v-icon>
                                    </button>
                                </template>
                                <span>מחיקה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="co-empty">
                            <v-progress-linear v-if="progressShow" indeterminate color="#fdb721" height="3"
                                class="co-empty__progress"></v-progress-linear>
                            <template v-else-if="communities.length">
                                <div class="co-empty__icon"><v-icon size="30">mdi-check-circle-outline</v-icon></div>
                                <h3 class="co-empty__title">אין קהילות בסינון הזה</h3>
                                <button type="button" class="co-empty__btn" @click="setQuickFilter('all')">הצג את כל הקהילות</button>
                            </template>
                            <template v-else>
                                <div class="co-empty__icon"><v-icon size="30">mdi-account-group-outline</v-icon></div>
                                <h3 class="co-empty__title">לא נמצאו קהילות</h3>
                                <p class="co-empty__hint">נסה לרענן, או הוסף קהילה חדשה</p>
                                <button type="button" class="co-empty__btn" @click="getCommunities()">רענון</button>
                            </template>
                        </div>
                    </template>

                    <template v-slot:no-results>
                        <div class="co-empty">
                            <div class="co-empty__icon"><v-icon size="30">mdi-magnify-close</v-icon></div>
                            <h3 class="co-empty__title">לא נמצאו תוצאות</h3>
                            <p class="co-empty__hint">נסה מילות חיפוש אחרות</p>
                        </div>
                    </template>
                </v-data-table>

                <div v-if="pageCount > 1" class="co-pagination-wrap">
                    <v-pagination v-model="currentPage" color="#0d2c6d" circle :length="pageCount"
                        :total-visible="7"></v-pagination>
                </div>
            </div>
        </main>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>

        <AddEditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @communityCreated="newCommunitySaved" @communityEdited="editedCommunitySaved"> </AddEditDialog>

        <AlertDialog v-model="alertDialog" v-if="alertDialog" :item="itemToDelete" @itemDeleted="communityDeleted" />
    </div>
</template>

<script>

import SnackBar from '@/components/widgets/snackBar.vue'
import AddEditDialog from '@/components/admin/communities/dialogs/AddEditDialog'
import AlertDialog from '@/components/admin/communities/dialogs/AlertDialog'
import SharedFunctions from "@/shared/SharedFunctions";

export default {
    components: {
        SnackBar,
        AddEditDialog,
        AlertDialog
    },
    data: () => ({
        headers: [
            { text: '#', value: 'index', align: 'center', sortable: false },
            { text: 'שם', value: 'name', align: 'center' },
            { text: 'שם במערכת לחומרא', value: 'external_name', align: 'center' },
            { text: 'מזהה במערכת לחומרא', value: 'external_id', align: 'center' },
            { text: 'טוקן', value: 'token', align: 'center', sortable: false },
            { text: 'רמות סינון', value: 'levels', align: 'center', sortable: false },
            { text: 'פעולות', value: 'actions', align: 'center', sortable: false },
        ],
        communities: [],
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        search: "",
        quickFilter: "all",
        revealedTokens: {},
        alertDialog: false,
        addEditDialog: false,
        itemToDelete: null,
        editedItem: {},
        formTitle: "",
        progressShow: false,
        currentPage: 1,
        pageCount: 0,
        itemsPerPage: 20,
    }),
    computed: {
        filteredCommunities() {
            if (this.quickFilter === 'noLevels') {
                return this.communities.filter(community => !this.levelsCount(community));
            }
            if (this.quickFilter === 'notSynced') {
                return this.communities.filter(community => !community.external_name);
            }
            return this.communities;
        },
        totalLevels() {
            return this.communities.reduce((sum, community) => sum + this.levelsCount(community), 0);
        },
        noLevelsCount() {
            return this.communities.filter(community => !this.levelsCount(community)).length;
        },
        notSyncedCount() {
            return this.communities.filter(community => !community.external_name).length;
        },
        quickFilterLabel() {
            return this.quickFilter === 'noLevels' ? 'ללא רמות סינון'
                : this.quickFilter === 'notSynced' ? 'לא מסונכרנות עם לחומרא' : '';
        },
    },
    watch: {
        quickFilter() {
            this.currentPage = 1;
        },
    },
    methods: {
        async getCommunities() {
            this.progressShow = true;
            this.communities = await SharedFunctions.getCommunities();
            this.progressShow = false;
        },
        openEditDialog(item) {
            const originalItem = this.communities.find(community => community._id === item._id);
            this.editedItem = originalItem;
            this.formTitle = "ערוך קהילה"
            this.addEditDialog = true
        },
        openCreateDIalog() {
            this.formTitle = "הוסף קהילה חדשה"
            this.editedItem = {}
            this.addEditDialog = true
        },
        openDeleteDialog(item) {
            this.itemToDelete = item
            this.formTitle = "האם אתה בטוח שברצונך למחוק את הקהילה?"
            this.alertDialog = true
        },
        newCommunitySaved(item, text, color) {
            this.showSnackBar(text, color)
            if (item === "") {
                return
            }
            this.communities.push(item);
        },
        editedCommunitySaved(item, text, color) {
            this.showSnackBar(text, color)
            if (item === "") {
                return
            }
            const index = this.communities.findIndex(community => community._id === item._id);
            if (index === -1) {
                return
            }
            // $set כדי שגם שדות חדשים (כמו levels אחרי הוספה) יתעדכנו בטבלה
            this.$set(this.communities, index, { ...this.communities[index], ...item });
        },
        communityDeleted(item, text, color) {
            if (!item) {
                this.showSnackBar(text, color)
                return
            }
            const index = this.communities.findIndex(community => community._id === item._id);
            if (index !== -1) {
                this.communities.splice(index, 1);
            }
            this.showSnackBar(text, color)
        },
        setQuickFilter(filter) {
            // לחיצה על הסינון הפעיל מבטלת אותו
            this.quickFilter = this.quickFilter === filter ? 'all' : filter;
        },
        levelsCount(community) {
            return Array.isArray(community.levels) ? community.levels.length : 0;
        },
        levelsLabel(community) {
            const count = this.levelsCount(community);
            if (!count) return 'אין רמות';
            return count === 1 ? 'רמה אחת' : `${count} רמות`;
        },
        sortedLevels(community) {
            return [...(community.levels || [])].sort((a, b) => Number(a.level_code) - Number(b.level_code));
        },
        initial(name) {
            return (name || '').trim().charAt(0) || '?';
        },
        maskToken(token) {
            const value = String(token || '');
            return value.length <= 4 ? '••••' : '••••••' + value.slice(-4);
        },
        toggleToken(item) {
            this.$set(this.revealedTokens, item._id, !this.revealedTokens[item._id]);
        },
        async copyToken(item) {
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(item.token);
                } else {
                    const textarea = document.createElement('textarea');
                    textarea.value = item.token;
                    textarea.setAttribute('readonly', '');
                    textarea.style.position = 'fixed';
                    textarea.style.opacity = '0';
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                }
                this.showSnackBar("הטוקן הועתק", "green");
            } catch (error) {
                this.showSnackBar("לא ניתן להעתיק את הטוקן", "red");
            }
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true
        },
    },
    created() {
        this.getCommunities();
    },
}
</script>

<style scoped>
/* ─────────────────────── TOKENS ─────────────────────── */
.co-page {
    --c-navy: #0d2c6d;
    --c-gold: #fdb721;
    --c-gold-deep: #e09c0b;
    --c-gold-soft: rgba(253, 183, 33, 0.14);
    --c-purple: #8066f0;
    --c-purple-soft: rgba(128, 102, 240, 0.10);
    --c-indigo: #3F51B5;
    --c-indigo-soft: rgba(63, 81, 181, 0.10);
    --c-bg: #f5f7fb;
    --c-surface: #ffffff;
    --c-border: #e3e8ef;
    --c-border-soft: #eef2f7;
    --c-text: #0f172a;
    --c-text-soft: #475569;
    --c-text-mute: #94a3b8;
    --c-amber: #b45309;
    --c-amber-soft: rgba(217, 119, 6, 0.10);
    --c-red: #c74747;
    --c-red-soft: rgba(199, 71, 71, 0.10);

    --c-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --c-ease: cubic-bezier(0.22, 1, 0.36, 1);

    min-height: 100vh;
    padding-bottom: 60px;
    background: var(--c-bg);
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────────────── HEADER ─────────────────────── */
.co-header {
    position: relative;
    background:
        radial-gradient(800px 200px at 90% -30%, rgba(253, 183, 33, 0.20), transparent 60%),
        linear-gradient(135deg, #0d2c6d 0%, #13306f 55%, #1b3a80 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--c-shadow-md);
}

.co-header__inner {
    max-width: 1500px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.co-header__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.co-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.co-total-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}
.co-total-pill ::v-deep .v-icon { color: rgba(255, 255, 255, 0.85) !important; }

.co-cta {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 16px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: var(--c-gold);
    color: #2d1f00;
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.22s var(--c-ease);
    flex-shrink: 0;
}
.co-cta:hover {
    background: #ffc53d;
    transform: translateY(-1px);
    box-shadow: 0 6px 14px -4px rgba(253, 183, 33, 0.6);
}
.co-cta ::v-deep .v-icon { color: #2d1f00 !important; }

/* ─────────────────────── MAIN + STATS ─────────────────────── */
.co-main {
    max-width: 1500px;
    margin: 22px auto 0;
    padding: 0 22px;
}

.co-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.co-stat {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 70px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--c-surface);
    border: 1px solid var(--c-border-soft);
    box-shadow: var(--c-shadow-md);
    font-family: inherit;
    text-align: right;
    cursor: pointer;
    transition: border-color 0.2s var(--c-ease), transform 0.2s var(--c-ease), box-shadow 0.2s var(--c-ease);
    animation: co-cardIn 0.45s var(--c-ease) backwards;
}
.co-stat:nth-child(1) { animation-delay: 0.00s; }
.co-stat:nth-child(2) { animation-delay: 0.06s; }
.co-stat:nth-child(3) { animation-delay: 0.12s; }
.co-stat:nth-child(4) { animation-delay: 0.18s; }

.co-stat:not(.co-stat--static):hover {
    transform: translateY(-1px);
    border-color: var(--c-border);
}
.co-stat--static { cursor: default; }

.co-stat--active {
    border-color: var(--c-navy) !important;
    box-shadow: 0 0 0 3px rgba(13, 44, 109, 0.10), var(--c-shadow-md);
}

@keyframes co-cardIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}

.co-stat__icon {
    width: 40px; height: 40px;
    flex-shrink: 0;
    display: grid; place-items: center;
    border-radius: 11px;
}
.co-stat__body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.co-stat__label {
    color: var(--c-text-mute);
    font-size: 0.76rem;
    font-weight: 700;
}
.co-stat__value {
    color: var(--c-text);
    font-size: 1.3rem;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.01em;
    font-feature-settings: "tnum" 1;
}

.co-stat--all    .co-stat__icon { color: var(--c-navy);   background: rgba(13, 44, 109, 0.08); }
.co-stat--levels .co-stat__icon { color: var(--c-purple); background: var(--c-purple-soft); }
.co-stat--warn   .co-stat__icon { color: var(--c-amber);  background: var(--c-amber-soft); }
.co-stat--sync   .co-stat__icon { color: var(--c-indigo); background: var(--c-indigo-soft); }

/* ─────────────────────── TOOLBAR ─────────────────────── */
.co-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
}

.co-toolbar__end {
    display: flex;
    align-items: center;
    gap: 12px;
}

.co-search {
    position: relative;
    display: flex;
    align-items: center;
    height: 42px;
    padding: 0 12px;
    border-radius: 12px;
    background: var(--c-surface);
    border: 1px solid var(--c-border);
    transition: border-color 0.2s var(--c-ease), box-shadow 0.2s var(--c-ease);
}
.co-search:focus-within {
    border-color: var(--c-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.10);
}
.co-search__icon { color: var(--c-text-mute) !important; }
.co-search__input {
    flex: 1;
    min-width: 0;
    padding: 0 9px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--c-text);
    font-family: inherit;
    font-size: 0.91rem;
}
.co-search__input::placeholder { color: var(--c-text-mute); }
.co-search__clear {
    width: 22px; height: 22px;
    display: grid; place-items: center;
    border: none;
    border-radius: 50%;
    background: var(--c-border-soft);
    color: var(--c-text-soft);
    cursor: pointer;
}

.co-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px 5px 12px;
    border-radius: 999px;
    background: rgba(13, 44, 109, 0.08);
    color: var(--c-navy);
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
}
.co-filter-chip ::v-deep .v-icon { color: var(--c-navy) !important; }
.co-filter-chip__clear {
    width: 20px; height: 20px;
    display: grid; place-items: center;
    border: none;
    border-radius: 50%;
    background: rgba(13, 44, 109, 0.10);
    cursor: pointer;
}

.co-pagesize {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--c-text-soft);
    font-size: 0.84rem;
    font-weight: 700;
}
.co-pagesize__select {
    padding: 7px 12px;
    border: 1px solid var(--c-border);
    border-radius: 10px;
    background: var(--c-surface);
    color: var(--c-text);
    font-family: inherit;
    font-size: 0.87rem;
    font-weight: 700;
    cursor: pointer;
    outline: none;
}
.co-pagesize__select:focus {
    border-color: var(--c-navy);
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.10);
}

/* ─────────────────────── TABLE ─────────────────────── */
.co-table-card {
    overflow: hidden;
    border: 1px solid var(--c-border-soft);
    border-radius: 18px;
    background: var(--c-surface);
    box-shadow: var(--c-shadow-md);
}

::v-deep #table1.v-data-table {
    background: transparent !important;
}
::v-deep #table1.v-data-table .v-data-table-header th {
    height: 52px !important;
    border-bottom: 1px solid var(--c-border-soft) !important;
    border-left: none !important;
    background: #fafbfd !important;
    text-align: center !important;
}
::v-deep #table1.v-data-table .v-data-table-header th .v-data-table-header__icon {
    color: var(--c-text-mute) !important;
    opacity: 1 !important;
}

.co-th {
    color: var(--c-text-soft) !important;
    font-family: inherit !important;
    font-size: 0.78rem !important;
    font-weight: 800 !important;
    letter-spacing: 0.02em;
}

::v-deep #table1.v-data-table tbody tr {
    transition: background 0.18s var(--c-ease);
}
::v-deep #table1.v-data-table tbody tr:hover {
    background: #fafbfd !important;
}
::v-deep #table1.v-data-table tbody tr td {
    height: 64px;
    padding: 11px 12px !important;
    border-bottom: 1px solid var(--c-border-soft) !important;
    color: var(--c-text);
    font-family: inherit;
    font-size: 0.88rem;
    text-align: center;
}

.co-index {
    color: var(--c-text-mute);
    font-size: 0.82rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}
.co-muted { color: var(--c-text-mute); }

.co-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: flex-start;
    min-width: 0;
    padding-right: 4px;
}
.co-avatar {
    flex-shrink: 0;
    width: 34px; height: 34px;
    border-radius: 10px;
    display: grid; place-items: center;
    color: #fff;
    font-weight: 800;
    font-size: 0.88rem;
    background: linear-gradient(135deg, #0d2c6d 0%, #1f3a8a 100%);
    box-shadow: 0 2px 6px -2px rgba(13, 44, 109, 0.30);
    user-select: none;
}
.co-name {
    font-weight: 700;
    color: var(--c-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.co-ext-name {
    color: var(--c-text-soft);
    font-weight: 600;
}

.co-num-cell {
    direction: ltr;
    display: inline-block;
    color: var(--c-text);
    font-feature-settings: "tnum" 1;
    font-weight: 700;
}

/* token */
.co-token {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    direction: ltr;
}
.co-token__value {
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 3px 8px;
    border-radius: 7px;
    background: #f3f5f9 !important;
    color: var(--c-text-soft) !important;
    font-size: 0.8rem;
    font-weight: 600;
    box-shadow: none !important;
}
.co-token__btn {
    width: 26px; height: 26px;
    display: grid; place-items: center;
    border: none;
    border-radius: 7px;
    background: transparent;
    cursor: pointer;
    transition: background 0.18s var(--c-ease);
}
.co-token__btn ::v-deep .v-icon { color: var(--c-text-mute) !important; }
.co-token__btn:hover { background: var(--c-border-soft); }
.co-token__btn:hover ::v-deep .v-icon { color: var(--c-navy) !important; }

/* levels chip */
.co-levels-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 11px;
    border-radius: 999px;
    border: 1px solid rgba(128, 102, 240, 0.22);
    background: var(--c-purple-soft);
    color: #5b45d6;
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: 800;
    cursor: pointer;
    transition: transform 0.18s var(--c-ease), box-shadow 0.18s var(--c-ease);
}
.co-levels-chip ::v-deep .v-icon { color: inherit !important; }
.co-levels-chip:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px -4px rgba(128, 102, 240, 0.45);
}
.co-levels-chip--empty {
    border-color: rgba(217, 119, 6, 0.25);
    background: var(--c-amber-soft);
    color: var(--c-amber);
}
.co-levels-chip--empty:hover { box-shadow: 0 4px 10px -4px rgba(217, 119, 6, 0.40); }

.co-levels-tip {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 2px 0;
}
.co-levels-tip__row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.co-levels-tip__code {
    min-width: 34px;
    text-align: center;
    padding: 0 6px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.16);
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}

/* ─────────────────────── ACTIONS ─────────────────────── */
.co-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}
.co-action-btn {
    width: 32px; height: 32px;
    display: grid; place-items: center;
    border: 1px solid var(--c-border);
    border-radius: 9px;
    background: var(--c-surface);
    color: var(--c-text-soft);
    cursor: pointer;
    transition: all 0.18s var(--c-ease);
}
.co-action-btn ::v-deep .v-icon { color: inherit !important; }
.co-action-btn:hover {
    border-color: transparent;
    color: #fff;
    transform: translateY(-1px);
}
.co-action-btn--edit:hover {
    background: var(--c-navy);
    box-shadow: 0 5px 12px -4px rgba(13, 44, 109, 0.45);
}
.co-action-btn--delete:hover {
    background: var(--c-red);
    box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.45);
}

/* ─────────────────────── EMPTY STATES ─────────────────────── */
.co-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 46px 20px;
}
.co-empty__progress { max-width: 320px; border-radius: 3px; }
.co-empty__icon {
    width: 58px; height: 58px;
    display: grid; place-items: center;
    border-radius: 16px;
    background: rgba(13, 44, 109, 0.06);
    margin-bottom: 6px;
}
.co-empty__icon ::v-deep .v-icon { color: var(--c-navy) !important; }
.co-empty__title {
    margin: 0;
    color: var(--c-text);
    font-size: 1rem;
    font-weight: 800;
}
.co-empty__hint {
    margin: 0;
    color: var(--c-text-mute);
    font-size: 0.86rem;
}
.co-empty__btn {
    margin-top: 10px;
    padding: 7px 18px;
    border: 1px solid var(--c-border);
    border-radius: 999px;
    background: var(--c-surface);
    color: var(--c-navy);
    font-family: inherit;
    font-size: 0.86rem;
    font-weight: 700;
    cursor: pointer;
}
.co-empty__btn:hover { background: #f3f5f9; }

.co-pagination-wrap {
    padding: 12px 0 14px;
    border-top: 1px solid var(--c-border-soft);
}

/* ─────────────────────── RESPONSIVE ─────────────────────── */
@media (max-width: 960px) {
    .co-stats { grid-template-columns: repeat(2, minmax(140px, 1fr)); }
    .co-toolbar { grid-template-columns: 1fr; }
    .co-toolbar__end { justify-content: space-between; }
}

@media (max-width: 600px) {
    .co-header__inner { padding: 0 16px; }
    .co-main { padding: 0 12px; }
    .co-cta span { display: none; }
    .co-cta { padding: 8px; }
    ::v-deep #table1.v-data-table tbody tr td { padding: 9px 6px !important; font-size: 0.84rem; }
}

@media (prefers-reduced-motion: reduce) {
    .co-stat { animation: none; }
}
</style>